import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, CheckCircle2, Users, X } from "lucide-react";
import { Drawer, formatDate, formatMoney } from "./shared";

function empLabel(emp) {
  return (
    emp.full_name ||
    [emp.first_name, emp.last_name].filter(Boolean).join(" ") ||
    emp.employee_name ||
    emp.employee_code ||
    "—"
  );
}

function empId(emp) {
  return String(emp.employee_id || emp.id || "");
}

/**
 * Multi-step advance create:
 * Individual | Bulk → select → amounts → full-screen review → submit
 */
export default function AdvanceCreateWizard({
  open,
  enabled,
  saving,
  employees,
  eligibleIds,
  onClose,
  onSubmitIndividual,
  onSubmitBulk,
  onEnsureEligible,
  toastWarn,
}) {
  const [mode, setMode] = useState("individual"); // individual | bulk
  const [step, setStep] = useState(1); // bulk: 1 select, 2 amounts, 3 review
  const [pickerSearch, setPickerSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [amounts, setAmounts] = useState({}); // id -> amount string
  const [advanceDate, setAdvanceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [singleId, setSingleId] = useState("");
  const [singleAmount, setSingleAmount] = useState("");
  const [showReview, setShowReview] = useState(false);
  const [stepError, setStepError] = useState("");
  const [localEligible, setLocalEligible] = useState([]);
  const [continuing, setContinuing] = useState(false);

  const eligibleSet = useMemo(() => {
    const fromProps = (eligibleIds || []).map(String);
    const merged = new Set([...fromProps, ...localEligible.map(String)]);
    return merged;
  }, [eligibleIds, localEligible]);

  const pickerList = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    return (employees || [])
      .map((emp) => ({
        id: empId(emp),
        name: empLabel(emp),
        code: emp.employee_code || "",
        department: emp.department_name || emp.department?.name || "",
        eligible: eligibleSet.has(empId(emp)),
        raw: emp,
      }))
      .filter((row) => row.id)
      .filter((row) => {
        if (!q) return true;
        return row.name.toLowerCase().includes(q) || String(row.code).toLowerCase().includes(q);
      })
      .sort((a, b) => Number(b.eligible) - Number(a.eligible) || a.name.localeCompare(b.name));
  }, [employees, eligibleSet, pickerSearch]);

  const selectedRows = useMemo(
    () => pickerList.filter((row) => selectedIds.includes(row.id)),
    [pickerList, selectedIds]
  );

  const reviewRows = useMemo(() => {
    return selectedIds
      .map((id) => {
        const row = pickerList.find((r) => r.id === id) || {
          id,
          name: "—",
          code: "",
          eligible: eligibleSet.has(id),
        };
        return {
          ...row,
          amount: Number(amounts[id]),
        };
      })
      .filter((r) => selectedIds.includes(r.id));
  }, [selectedIds, pickerList, amounts, eligibleSet]);

  const totalAmount = useMemo(
    () => reviewRows.reduce((sum, r) => sum + (Number.isFinite(r.amount) ? r.amount : 0), 0),
    [reviewRows]
  );

  function reset() {
    setMode("individual");
    setStep(1);
    setPickerSearch("");
    setSelectedIds([]);
    setAmounts({});
    setAdvanceDate(new Date().toISOString().slice(0, 10));
    setNote("");
    setSingleId("");
    setSingleAmount("");
    setShowReview(false);
    setStepError("");
    setLocalEligible([]);
    setContinuing(false);
  }

  function handleClose() {
    if (saving || continuing) return;
    reset();
    onClose();
  }

  function toggleId(id) {
    setStepError("");
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
    setAmounts((prev) => (prev[id] != null ? prev : { ...prev, [id]: "" }));
  }

  function selectAllVisible() {
    setStepError("");
    const ids = pickerList.map((r) => r.id);
    if (!ids.length) {
      const msg = "No employees in this list.";
      setStepError(msg);
      toastWarn?.(msg);
      return;
    }
    setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
    setAmounts((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        if (next[id] == null) next[id] = "";
      });
      return next;
    });
  }

  function clearSelection() {
    setStepError("");
    setSelectedIds([]);
    setAmounts({});
  }

  async function goAmounts() {
    setStepError("");
    if (!selectedIds.length) {
      const msg = "Select at least one employee to continue.";
      setStepError(msg);
      toastWarn?.(msg);
      return;
    }
    const ineligible = selectedIds.filter((id) => !eligibleSet.has(String(id)));
    if (ineligible.length && typeof onEnsureEligible === "function") {
      setContinuing(true);
      try {
        const ok = await onEnsureEligible(ineligible);
        if (!ok) {
          const msg =
            "Could not enable Advance Taken for the selected employees. Enable them under the Employees tab, then try again.";
          setStepError(msg);
          toastWarn?.(msg);
          return;
        }
        setLocalEligible((prev) => Array.from(new Set([...prev.map(String), ...ineligible.map(String)])));
      } catch (err) {
        const msg = "Could not enable selected employees. Please try again.";
        setStepError(msg);
        toastWarn?.(msg);
        return;
      } finally {
        setContinuing(false);
      }
    } else if (ineligible.length) {
      const msg =
        "Selected employees are not eligible for Advance Taken. Enable them under the Employees tab, or use Continue again after enabling.";
      setStepError(msg);
      toastWarn?.(msg);
      return;
    }
    setStep(2);
  }

  function goReview() {
    setStepError("");
    if (!advanceDate) {
      const msg = "Advance date is required.";
      setStepError(msg);
      toastWarn?.(msg);
      return;
    }
    const bad = selectedIds.filter((id) => {
      const n = Number(amounts[id]);
      return !Number.isFinite(n) || n <= 0;
    });
    if (bad.length) {
      const msg = "Enter a valid amount greater than 0 for every selected employee.";
      setStepError(msg);
      toastWarn?.(msg);
      return;
    }
    setShowReview(true);
    setStep(3);
  }

  async function confirmBulk() {
    const payload = selectedIds.map((id) => ({
      employee_id: id,
      amount: Number(amounts[id]),
      advance_date: advanceDate,
      note: note.trim() || null,
    }));
    const ok = await onSubmitBulk(payload);
    if (ok) reset();
  }

  async function submitIndividual(e) {
    e.preventDefault();
    setStepError("");
    if (!singleId) {
      const msg = "Select an employee.";
      setStepError(msg);
      toastWarn?.(msg);
      return;
    }
    if (!eligibleSet.has(String(singleId)) && typeof onEnsureEligible === "function") {
      setContinuing(true);
      try {
        const ok = await onEnsureEligible([singleId]);
        if (!ok) {
          const msg = "Could not enable this employee for Advance Taken.";
          setStepError(msg);
          toastWarn?.(msg);
          return;
        }
        setLocalEligible((prev) => Array.from(new Set([...prev.map(String), String(singleId)])));
      } finally {
        setContinuing(false);
      }
    }
    const amount = Number(singleAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      const msg = "Advance amount must be greater than 0.";
      setStepError(msg);
      toastWarn?.(msg);
      return;
    }
    if (!advanceDate) {
      const msg = "Advance date is required.";
      setStepError(msg);
      toastWarn?.(msg);
      return;
    }
    const ok = await onSubmitIndividual({
      employee_id: singleId,
      amount,
      advance_date: advanceDate,
      note: note.trim() || null,
    });
    if (ok) reset();
  }

  const drawerFooter =
    mode === "individual" ? (
      <>
        <button type="button" className="ab-btn ab-btn--secondary" disabled={saving} onClick={handleClose}>
          Cancel
        </button>
        <button type="submit" form="adv-individual-form" className="ab-btn ab-btn--primary" disabled={saving || !enabled}>
          {saving ? "Saving…" : "Save advance"}
        </button>
      </>
    ) : step === 1 ? (
      <>
        <button type="button" className="ab-btn ab-btn--secondary" disabled={saving || continuing} onClick={handleClose}>
          Cancel
        </button>
        <button
          type="button"
          className="ab-btn ab-btn--primary"
          disabled={saving || continuing || !enabled}
          onClick={() => {
            void goAmounts();
          }}
        >
          {continuing ? "Enabling…" : `Continue · ${selectedIds.length} selected`}
        </button>
      </>
    ) : (
      <>
        <button
          type="button"
          className="ab-btn ab-btn--secondary"
          disabled={saving || continuing}
          onClick={() => {
            setStepError("");
            setStep(1);
          }}
        >
          Back
        </button>
        <button type="button" className="ab-btn ab-btn--primary" disabled={saving || !enabled} onClick={goReview}>
          Review &amp; submit
        </button>
      </>
    );

  const reviewNode = showReview ? (
    <div className="ab-review-overlay" role="dialog" aria-modal="true" aria-labelledby="adv-review-title">
      <div className="ab-review-panel">
        <div className="ab-review-panel__head">
          <div>
            <h2 id="adv-review-title">Confirm bulk advances</h2>
            <p>
              Review each employee amount. Advances stay pending until approved, then deduct once in payroll.
            </p>
          </div>
          <button
            type="button"
            className="ab-btn ab-btn--ghost ab-btn--sm"
            disabled={saving}
            onClick={() => {
              setShowReview(false);
              setStep(2);
            }}
            aria-label="Close review"
          >
            <X size={16} />
          </button>
        </div>

        <div className="ab-review-kpis">
          <div className="ab-review-kpi ab-review-kpi--orange">
            <span>Employees</span>
            <strong>{reviewRows.length}</strong>
          </div>
          <div className="ab-review-kpi ab-review-kpi--blue">
            <span>Advance date</span>
            <strong style={{ fontSize: "1rem" }}>{formatDate(advanceDate)}</strong>
          </div>
          <div className="ab-review-kpi ab-review-kpi--blue">
            <span>Total amount</span>
            <strong>{formatMoney(totalAmount)}</strong>
          </div>
        </div>

        <div className="ab-review-body">
          {note ? (
            <p className="ab-note" style={{ marginBottom: "0.75rem" }}>
              Shared note: <strong>{note}</strong>
            </p>
          ) : null}
          <table className="ab-review-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Employee</th>
                <th>Code</th>
                <th>Advance amount</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {reviewRows.map((row, idx) => (
                <tr key={row.id}>
                  <td>{idx + 1}</td>
                  <td>
                    <strong>{row.name}</strong>
                  </td>
                  <td>{row.code || "—"}</td>
                  <td>
                    <strong>{formatMoney(row.amount)}</strong>
                  </td>
                  <td>{formatDate(advanceDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ab-review-foot">
          <button
            type="button"
            className="ab-btn ab-btn--secondary"
            disabled={saving}
            onClick={() => {
              setShowReview(false);
              setStep(2);
            }}
          >
            Edit amounts
          </button>
          <div className="ab-row-actions">
            <button type="button" className="ab-btn ab-btn--ghost" disabled={saving} onClick={handleClose}>
              Discard
            </button>
            <button type="button" className="ab-btn ab-btn--primary" disabled={saving || !enabled} onClick={confirmBulk}>
              {saving ? "Submitting…" : `Confirm ${reviewRows.length} advances`}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <Drawer
        open={open && !showReview}
        title="Add advance"
        subtitle="Pending until approved. Only approved amounts are deducted in payroll."
        onClose={handleClose}
        wide
        footer={drawerFooter}
      >
        <div className="ab-mode-toggle" role="tablist" aria-label="Advance entry mode">
          <button
            type="button"
            className={mode === "individual" ? "ab-mode-toggle--on" : ""}
            onClick={() => {
              setMode("individual");
              setStep(1);
              setShowReview(false);
            }}
          >
            Individual
          </button>
          <button
            type="button"
            className={mode === "bulk" ? "ab-mode-toggle--on" : ""}
            onClick={() => {
              setMode("bulk");
              setStep(1);
              setShowReview(false);
            }}
          >
            <Users size={13} style={{ marginRight: 4, verticalAlign: "middle" }} />
            Bulk employees
          </button>
        </div>

        {mode === "bulk" ? (
          <div className="ab-steps" aria-hidden>
            <span className={`ab-step${step >= 1 ? (step === 1 ? " ab-step--active" : " ab-step--done") : ""}`}>
              <span className="ab-step__num">{step > 1 ? <CheckCircle2 size={12} /> : "1"}</span>
              Select
            </span>
            <span className={`ab-step${step >= 2 ? (step === 2 ? " ab-step--active" : " ab-step--done") : ""}`}>
              <span className="ab-step__num">{step > 2 ? <CheckCircle2 size={12} /> : "2"}</span>
              Amounts
            </span>
            <span className={`ab-step${step === 3 ? " ab-step--active" : ""}`}>
              <span className="ab-step__num">3</span>
              Review
            </span>
          </div>
        ) : null}

        {stepError ? (
          <div className="ab-warn-box" role="alert">
            <AlertTriangle size={16} />
            <span>{stepError}</span>
          </div>
        ) : null}

        {mode === "individual" ? (
          <form id="adv-individual-form" className="ab-form-grid" onSubmit={submitIndividual}>
            <div className="ab-field ab-field--full">
              <label>Employee</label>
              <select
                className="ab-select"
                required
                value={singleId}
                onChange={(e) => setSingleId(e.target.value)}
              >
                <option value="">Select employee</option>
                {(employees || []).map((emp) => {
                  const id = empId(emp);
                  const eligible = eligibleSet.has(id);
                  return (
                    <option key={id} value={id}>
                      {empLabel(emp)}
                      {emp.employee_code ? ` · ${emp.employee_code}` : ""}
                      {eligible ? "" : " (not eligible)"}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="ab-field">
              <label>Amount</label>
              <input
                type="number"
                className="ab-input"
                min="0.01"
                step="0.01"
                required
                value={singleAmount}
                onChange={(e) => setSingleAmount(e.target.value)}
              />
            </div>
            <div className="ab-field">
              <label>Advance date</label>
              <input
                type="date"
                className="ab-input"
                required
                value={advanceDate}
                onChange={(e) => setAdvanceDate(e.target.value)}
              />
            </div>
            <div className="ab-field ab-field--full">
              <label>Reference / note</label>
              <input
                className="ab-input"
                maxLength={500}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </form>
        ) : null}

        {mode === "bulk" && step === 1 ? (
          <div>
            <div className="ab-row-actions" style={{ marginBottom: "0.65rem" }}>
              <button type="button" className="ab-btn ab-btn--secondary ab-btn--sm" onClick={selectAllVisible}>
                Select all eligible
              </button>
              <button type="button" className="ab-btn ab-btn--ghost ab-btn--sm" onClick={clearSelection}>
                Clear selection
              </button>
            </div>
            <div className="ab-emp-picker">
              <div className="ab-emp-picker__search">
                <input
                  className="ab-input"
                  placeholder="Search by name or employee code…"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                />
              </div>
              <div className="ab-emp-picker__list" role="listbox" aria-multiselectable="true">
                {pickerList.length === 0 ? (
                  <div style={{ padding: "1rem", fontSize: "0.82rem", color: "#64748b" }}>No employees match.</div>
                ) : (
                  pickerList.map((row) => {
                    const checked = selectedIds.includes(row.id);
                    return (
                      <label
                        key={row.id}
                        className={`ab-emp-picker__row${checked ? " is-selected" : ""}`}
                      >
                        <input type="checkbox" checked={checked} onChange={() => toggleId(row.id)} />
                        <span className="ab-emp-picker__meta">
                          <strong>{row.name}</strong>
                          <span>
                            {row.code || "—"}
                            {row.department ? ` · ${row.department}` : ""}
                            {row.eligible ? "" : " · Not eligible"}
                          </span>
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
            {selectedIds.length ? (
              <div className="ab-selected-chips" aria-label="Selected employees">
                {selectedRows.map((row) => (
                  <span key={row.id} className="ab-chip">
                    {row.name}
                    <button type="button" aria-label={`Remove ${row.name}`} onClick={() => toggleId(row.id)}>
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <div className="ab-warn-box">
                <AlertTriangle size={16} />
                <span>
                  Select employees with checkboxes, then click Continue. If they are not yet eligible, Continue will
                  enable Advance Taken for them automatically and move to amounts.
                </span>
              </div>
            )}
          </div>
        ) : null}

        {mode === "bulk" && step === 2 ? (
          <div>
            <div className="ab-form-grid" style={{ marginBottom: "0.85rem" }}>
              <div className="ab-field">
                <label>Advance date (shared)</label>
                <input
                  type="date"
                  className="ab-input"
                  required
                  value={advanceDate}
                  onChange={(e) => setAdvanceDate(e.target.value)}
                />
              </div>
              <div className="ab-field">
                <label>Shared note (optional)</label>
                <input
                  className="ab-input"
                  maxLength={500}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Applied to all selected"
                />
              </div>
            </div>
            <p className="ab-note" style={{ marginBottom: "0.5rem" }}>
              Enter an individual amount for each selected employee.
            </p>
            <div className="ab-amount-rows">
              {selectedRows.map((row) => (
                <div key={row.id} className="ab-amount-row">
                  <div>
                    <strong style={{ display: "block", fontSize: "0.85rem", color: "#0f172a" }}>{row.name}</strong>
                    <span className="ab-note">{row.code || "—"}</span>
                  </div>
                  <input
                    type="number"
                    className="ab-input"
                    min="0.01"
                    step="0.01"
                    placeholder="Amount"
                    value={amounts[row.id] ?? ""}
                    onChange={(e) => setAmounts((prev) => ({ ...prev, [row.id]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </Drawer>

      {typeof document !== "undefined" && reviewNode ? createPortal(reviewNode, document.body) : null}
    </>
  );
}
